import {
    initClient,
    initSubscriptionOffers,
    setAssetDomain,
    thewest,
} from '@news-mono/component-library'
import {
    authCheckAccessToken,
    ConsentLevel,
    loadUserLocation,
    retrieveLocationQueryString,
    initSubscriptionOffersAsync,
    Store,
} from '@news-mono/web-common'
import {
    isFeatureEnabled,
    toFeatureState,
} from '@west-australian-newspapers/etrigan-feature-toggles-client'
import { Logger } from '@west-australian-newspapers/typescript-log'

setAssetDomain()
// We need this to get the dependencies loaded in the correct order
require('../app/app')

declare global {
    interface Window {
        /** Refresh the authentication state, provided an access token. */
        refreshAuth?: (
            accessToken: string,
            version: string,
            platform: 'ios' | 'android',
            consent: ConsentLevel,
        ) => Promise<void>
        /** Logs out from the authentication state. */
        logoutAuth?: () => Promise<void>
        /** React Native Webview adapter. */
        ReactNativeWebView?: {
            postMessage: (data: string) => void
        }
    }
}

const { render, routeCacheRef } = initClient({
    version: { buildNumber: '', sha: '' },
    beforeInitialised: async ({ store, log, config, onEvent }) => {
        if (window.location.search.indexOf('location') !== -1) {
            const region = retrieveLocationQueryString(window.location)
            store.dispatch(loadUserLocation(region))
        }

        // Load subscription offers by dispatching init thunk request
        await loadOffers(store, log)

        // attempt to authenticate page on load.
        await store.dispatch(
            authCheckAccessToken({
                onEvent,
                config,
                log,
                invocation: 'auto',
                onInvocationEvent: () => {},
            }),
        )

        // notify app that it can update auth state.
        postWebviewMessage(true, store.getState().authentication.isLoggedIn)

        window.refreshAuth = async (
            accessToken,
            version,
            platform,
            consent,
        ) => {
            // Notify webview that it cannot refresh while already doing so.
            postWebviewMessage(
                false,
                store.getState().authentication.isLoggedIn,
            )
            await fetch(
                `/app/refresh?p=${platform}&version=${version}&c=${consent}`,
                {
                    method: 'POST',
                    body: JSON.stringify({ accessToken }),
                    credentials: 'include',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                },
            )

            await store.dispatch(
                authCheckAccessToken({
                    onEvent,
                    config,
                    log,
                    invocation: 'auto',
                    forceRefresh: true,
                    onInvocationEvent: () => {},
                }),
            )

            postWebviewMessage(true, store.getState().authentication.isLoggedIn)
        }

        window.logoutAuth = async () => {
            // Notify webview that it cannot refresh while logging out.
            postWebviewMessage(
                false,
                store.getState().authentication.isLoggedIn,
            )

            await fetch(`/app/logout`, {
                method: 'POST',
                credentials: 'include',
            })

            await store.dispatch(
                authCheckAccessToken({
                    onEvent,
                    config,
                    log,
                    invocation: 'auto',
                    forceRefresh: true,
                    onInvocationEvent: () => {},
                }),
            )

            postWebviewMessage(true, store.getState().authentication.isLoggedIn)
        }
    },
    requireApplication: () => require('../app/app').App,
    theme: thewest,
})

if (module.hot) {
    module.hot.accept('../app/app', () => {
        if (routeCacheRef) {
            routeCacheRef.clear()
        }

        setTimeout(render)
    })
}

const postWebviewMessage = (canUpdateAuth: boolean, isLoggedIn: boolean) => {
    window.ReactNativeWebView?.postMessage(
        JSON.stringify({
            type: 'authentication-ready',
            canUpdateAuth,
            isLoggedIn,
        }),
    )
}
async function loadOffers(store: Store, log: Logger) {
    try {
        const featureState = toFeatureState(store.getState().toggles)
        const rrmeEnabled = isFeatureEnabled(
            featureState,
            'google-rrme-full-override',
        )

        if (!rrmeEnabled) {
            log.info(
                { rrmeEnabled },
                'RRME disabled; skipping subscription offers init',
            )
            return
        }

        const DEFAULT_DEV_PUBLICATION_ID = 'thewest.dev.swmdigital.io'
        let publicationId = window.location.hostname.replace(/^www\./, '')
        if (
            publicationId.startsWith('localhost') ||
            publicationId.endsWith('.dev.swmdigital.io')
        ) {
            publicationId = DEFAULT_DEV_PUBLICATION_ID
        }
        await store.dispatch(
            initSubscriptionOffersAsync({
                publicationId,
                log,
                initFn: initSubscriptionOffers,
            }),
        )
    } catch (e) {
        log.warn({ e }, 'Failed to fetch subscription offers')
    }
}
